import React, { useState, useRef } from 'react';
import {
  Modal,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../hooks/useAppDispatch';

import AuthorizedSignCheckbox from '../authorized-sign-checkbox/AuthorizedSignCheckbox';
import styles from './CreateCustomerContactItemModal.module.scss';
import { createCustomerContactItem } from '../../slices/customers/customersSlice';
import { CustomerContactItem } from '../../models/customer.contact.model';
import { toast } from 'react-toastify';
import { hideOverlay, showOverlay } from '../../slices/show-progress-overlay/showProgressOverlay';

interface CreateContactModalProps {
  open: boolean;
  onClose: () => void;
  onSuccessfulSubmit?: () => void;
  customerId: number;
  companyId: number;
}

export function CreateCustomerContactItemModal({
  open,
  onClose,
  onSuccessfulSubmit,
  customerId,
  companyId,
}: CreateContactModalProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const formRef = useRef<HTMLDivElement | null>(null);

  const [formData, setFormData] = useState({
    full_name: '',
    email_address: '',
    phone_number: '',
    job_title: '',
    postal_address: '',
    authorized_rep: false,
    customer_id: customerId,
  });

  const [validationErrors, setValidationErrors] = useState({
    full_name: false,
  });

  const handleChange =
    <K extends keyof typeof formData>(field: K) =>
    (value: (typeof formData)[K]) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleCreate = () => {
    dispatch(showOverlay());
    const errors = {
      full_name: !formData.full_name.trim(),
    };

    setValidationErrors(errors);

    if (Object.values(errors).some((error) => error)) {
      return;
    }
    dispatch(
      createCustomerContactItem({
        customerId: customerId,
        contact: formData as Omit<CustomerContactItem, 'id'>,
        company_id: companyId,
      })
    )
      .unwrap()
      .then(() => {
        toast.success(t('messages.contactSuccessfullyCreated'));
        if (onSuccessfulSubmit) onSuccessfulSubmit();
        onClose();
      })
      .catch((error: any) =>
        toast.error(error.message ? error.message : t('messages.errorOccurred'))
      )
      .finally(() => {
        dispatch(hideOverlay());
      });
  };

  return (
    <Modal open={open} onClose={onClose} className={'action-modal'} closeAfterTransition>
      <Grid container className={styles.modal_container}>
        <Grid size={{ xs: 12, sm: 8, md: 6, lg: 4 }}>
          <Card ref={formRef}>
            <CardContent sx={{ paddingBottom: '16px !important' }}>
              <Typography variant='h6' gutterBottom>
                {t('headers.createCustomerContact')}
              </Typography>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label={`${t('labels.fullName')} *`}
                    variant='outlined'
                    fullWidth
                    value={formData.full_name ?? ''}
                    error={validationErrors.full_name}
                    helperText={validationErrors.full_name ? t('messages.requiredFullName') : ''}
                    onChange={(e) => handleChange('full_name')(e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label={`${t('formInputs.emailAddressInputLabel')}`}
                    variant='outlined'
                    type='email'
                    fullWidth
                    value={formData.email_address ?? ''}
                    onChange={(e) => handleChange('email_address')(e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label={`${t('labels.phoneNumber')}`}
                    variant='outlined'
                    type='tel'
                    fullWidth
                    value={formData.phone_number ?? ''}
                    onChange={(e) => handleChange('phone_number')(e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label={`${t('labels.jobTitle')}`}
                    variant='outlined'
                    fullWidth
                    value={formData.job_title ?? ''}
                    onChange={(e) => handleChange('job_title')(e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label={`${t('labels.postalAddress')}`}
                    variant='outlined'
                    fullWidth
                    value={formData.postal_address ?? ''}
                    onChange={(e) => handleChange('postal_address')(e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  {' '}
                  <AuthorizedSignCheckbox
                    authorizedRep
                    value={formData.authorized_rep}
                    onChange={handleChange('authorized_rep')}
                    disabled={false}
                  />
                </Grid>
              </Grid>

              <Stack direction='row' gap={'8px'} justifyContent='flex-end' marginTop={'16px'}>
                <Button
                  variant='contained'
                  className='btn-default btn-action-basic btn-cancel'
                  onClick={onClose}
                >
                  {t('buttons.close')}
                </Button>
                <Button
                  variant='contained'
                  className='btn-default btn-action-basic'
                  onClick={handleCreate}
                >
                  {t('buttons.create')}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Modal>
  );
}
